import { Spinner } from "@fluentui/react-components";
import { TeamsFxContext } from "../Context";
import WorkflowTaskCard from "./WorkflowTaskCard";
import { useContext, useEffect, useState, useRef } from "react";
import TabHeader from "../common/TabHeader";
import * as microsoftTeams from '@microsoft/teams-js';
import { logger } from "../../Logger";
import WorkflowTasksToolbar from "./WorkflowTasksToolbar";
import { Constants } from "../common/Constants";
import { useListObjects } from "../../hooks/useListObjects";
import { ListObjectRequestData } from "../../types/CommonTypes";
import { AnalyticsManager } from "../../services/analyticsUtility";
import AnalyticsConstants from "../common/AnalyticsConstants.json";

const sortFieldOptions = {
  startDate: {
    value: Constants.startDatePropName,
    label: ""
  },
  dueDate: {
    value: Constants.dueDatePropName,
    label: ""
  }
};

const WorkflowTasks: React.FunctionComponent = () => {
  // Tracking workflow tasks tab visit
  useEffect( () => {
    async function startProcessing() {
      if (teamsContext.configuration) { 
        const analyticsManager = AnalyticsManager.getInstance();
        analyticsManager.logPlace(AnalyticsConstants.tabWorkflowTasks);
      }
    }
    startProcessing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initalize teams app and notify success of loading
  useEffect(() => {
    (async () => {
      await microsoftTeams.app.initialize();
      microsoftTeams.app.notifySuccess();
    })();
  }, []);

  const teamsContext = useContext(TeamsFxContext);
  const i18n = teamsContext.i18n;
  sortFieldOptions.startDate.label = i18n.StartDate;
  sortFieldOptions.dueDate.label = i18n.DueDate;
  const [startIndex, setStartIndex] = useState<number>(0);
  const [sortField, setSortField] = useState<string>(sortFieldOptions.startDate.value);
  const listObjectInput: ListObjectRequestData = {
    ObjectType: Constants.workflowTask,
    SortCriteriaNameValuePairs: {
      sortField: sortField,
      sortOrder: Constants.sortOrder
    },
    MaxToLoad: 50,
    StartIndex: startIndex,
  };

  useEffect(() => {
    async function clearOnLogout() {
      if (!teamsContext?.teamcenter.isAuthenticated) {
        workflowTaskList.current = [];
        endReached.current = false;
        endIndex.current = 0;
        setStartIndex(0);
      }
    }
    clearOnLogout();
  }, [teamsContext?.teamcenter.isAuthenticated]);

  const workflowsApi = useListObjects(listObjectInput);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const scrollPosition = useRef<number>(0);
  const workflowTaskList = useRef<any[]>([]);
  const endIndex = useRef<number>(0);
  const endReached = useRef<boolean>(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);

  const clearList = () => {
    setStartIndex(0);
    workflowTaskList.current = [];
  };

  const onSortStatusChanged = (status: string) => {
    logger.logTrace("Entered onSortStatusChanged");
    clearList();
    setSortField(status);
    logger.logTrace("Exit onSortStatusChanged");
  };

  const onRefreshButtonClicked = () => {
    clearList();
    setShowSpinner(true);
    logger.logTrace("Entered onRefreshButtonClicked");
    workflowsApi?.refetch();
    logger.logTrace("Exit onRefreshButtonClicked");
    setShowSpinner(false);
  };

  const disableButton = () => {
    return (
      !teamsContext.teamcenter.isAuthenticated ||
      teamsContext.teamcenter.isAuthenticating ||
      !workflowsApi ||
      workflowsApi.isFetching
    );
  };

  const workflowTasksLoaded = (): boolean => {
    logger.logTrace("Entered workflowTasksLoaded");
    logger.logTrace("Exit workflowTasksLoaded");
    return (
      workflowsApi &&
      !workflowsApi.isRefetching &&
      !workflowsApi.isError &&
      workflowsApi.isFetched
    );
  };

  const noWorkflowTasksLoaded = (): boolean => {
    logger.logTrace("Entered noWorkflowTasksLoaded");
    logger.logTrace("Exit noWorkflowTasksLoaded");
    return (
      workflowsApi &&
      !workflowsApi.isError &&
      workflowsApi.isFetched &&
      !workflowsApi.listItems
    );
  };

  const loadMoreWorkflows = useRef(() => {
    logger.logTrace("Entered loadMoreProblems")
    workflowsApi.refetch();
    logger.logTrace("Exit loadMoreProblems")
  });

  // Open in Teamcenter button should be enabled only on desktop
  function isOpenInTcButtonEnabled() : boolean {
    return (
      teamsContext.clientType !== microsoftTeams.HostClientType.ios &&
      teamsContext.clientType !== microsoftTeams.HostClientType.android
    );
  }

  const showWorkflowTaskList = () => {
    const openInTcButtonEnabled = isOpenInTcButtonEnabled();
    if (workflowsApi && (workflowsApi.isLoading || workflowsApi.isRefetching)){
      if(workflowTaskList.current.length === 0) {
        return <Spinner appearance="inverted" />;
      } else {
        return (workflowTaskList.current);
      }
    }
    if (showSpinner) {
      return <Spinner appearance="inverted" />;
    }
    else if (workflowTasksLoaded() && workflowsApi.listItems) {
      const responseData = workflowsApi.listItems;
      if ('searchResults' in responseData && 'teamcenterUri' in responseData  && 'modelObjects' in responseData && 'cursor' in responseData) {
        const modelObjects: any[] = (responseData.modelObjects as any[]) || [];
        const searchResults: any[] = (responseData.searchResults as any[]) || [];
        const teamcenterUri = responseData.teamcenterUri;
        const cursor: any = (responseData.cursor as any) || {};
        if(cursor){
          endIndex.current = cursor.endIndex;
          endReached.current = cursor.endReached;
        }

        searchResults.forEach((workflowTask) => {
          if (workflowTask!=null &&workflowTask.props && workflowTask.props.root_target_attachments) {
            const targetObjectUids = workflowTask.props.root_target_attachments.dbValues;
            if(targetObjectUids !== null){
              for(let i=0; i<targetObjectUids.length; i++){
                const targetObject = modelObjects.find(
                  (object) =>
                    object.uid === targetObjectUids[i]
                );
                if (targetObject) {
                  const target = {
                    target_id: (targetObject?.props as any)?.item_id ?? null,
                    target_name: targetObject?.props?.object_name ?? null,
                    target_image_ticket: targetObject?.props?.awp0ThumbnailImageTicket ?? null,
                    target_uid: targetObjectUids[i]
                  };
                  workflowTask.target = [...(workflowTask.target ?? []), target]
                }
              }
            }
            workflowTask.teamcenterUri = teamcenterUri;
          }
        });
        const workflowList = searchResults
          .map((workflow) => (
            <WorkflowTaskCard
              key={workflow.uid}
              workflowTask={workflow}
              isOpenInTcButtonEnabled={openInTcButtonEnabled}
            />
          ));

        if(cursor.startIndex === 0){
          workflowTaskList.current = workflowList;
        }
        else if(workflowTaskList.current.length < (endIndex.current + 1)){
          workflowTaskList.current = [...workflowTaskList.current,...workflowList];
          window.scrollTo(0, scrollPosition.current);
        }
        return workflowTaskList.current;
      } else if (noWorkflowTasksLoaded()) {
        return (
          <div className="no-result-container">
            <p>{i18n.NoResultsFoundText}</p>
          </div>
        );
      }
    }
  };

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Check if the scroll position is at the bottom of the content
      if (scrollHeight - scrollTop - clientHeight < 20) {
        // Perform action when the scroll bar hits the bottom
        if(!endReached.current){
          scrollPosition.current = window.scrollY;
          setStartIndex( endIndex.current + 1);
        }
      }
    }
  };

  useEffect(() => {
    loadMoreWorkflows.current();
  }, [startIndex]);

  useEffect(() => {
    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll',handleScroll);
      return () => {
        element.removeEventListener('scroll', handleScroll);
      }
    }
  }, [teamsContext?.teamcenter.isAuthenticated]);

  useEffect(() => {
    const container = contentRef.current;
    // Check if the scroll bar is enabled after rendering
    const checkScrollBar = () => {
      if (container && !endReached.current) {
        if ((container.scrollHeight <= container.clientHeight)) {
          setStartIndex(endIndex.current + 1);
          // Call the function again after a delay (e.g., 5 seconds)
          setTimeout(checkScrollBar, 5000);
        }
      }
    };
    // Initial check after rendering
    checkScrollBar();
    // Listen for changes and update accordingly (e.g., window resize)
    const handleResize = () => {
      checkScrollBar();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array to run the effect only once after the initial render

  return (
    <>
      {teamsContext.teamcenter.isAuthenticated && (
        <div className="workflow-task-list">
          <div className="workflow-task-toolbar">
            <WorkflowTasksToolbar
              sortFieldOptions={sortFieldOptions}
              sortField={sortField}
              onSortStatusChanged={onSortStatusChanged}
              disableButton={disableButton}
              onRefreshButtonClicked={onRefreshButtonClicked}
            />
          </div>
          <div className="workflow-list-banner">
          <TabHeader/>
          </div>
          <div className="workflow-list"
            ref={contentRef}
          >{showWorkflowTaskList()}</div>
        </div>
      )}
    </>
  );
};

export default WorkflowTasks;
